// Client Cloudflare — les deux enregistrements DNS de l'infra client.
//
// La topologie vient de `docs/deploy.md` : le host du front est proxifié
// (orange, pour le cache edge du HTML SSR et la purge post-deploy), le host
// WordPress est DNS-only (gris) — Traefik y termine TLS via Let's Encrypt, et
// un proxy Cloudflare devant l'admin/GraphQL n'apporterait rien.

import type { Requester } from "./http"

const API = "https://api.cloudflare.com/client/v4"

export interface DnsRecordInput {
	name: string
	/** IPv4 du serveur Coolify. */
	content: string
	proxied: boolean
}

interface CloudflareEnvelope<T> {
	success: boolean
	errors: { code: number; message: string }[]
	result: T
}

export class CloudflareClient {
	constructor(
		private readonly zoneId: string,
		private readonly token: string,
		private readonly http: Requester,
	) {}

	private async call<T>(
		method: "GET" | "POST" | "PUT",
		path: string,
		label: string,
		body?: unknown,
		dryRunResult?: T,
	): Promise<T> {
		const res = await this.http.request<CloudflareEnvelope<T>>({
			method,
			url: `${API}${path}`,
			label,
			body,
			// Cloudflare enveloppe tout dans `{ success, errors, result }` : la
			// simulation doit avoir la même forme que le vrai retour, sinon le
			// dry-run casse là où le run réel passerait.
			dryRunResult: { success: true, errors: [], result: dryRunResult },
			headers: { authorization: `Bearer ${this.token}` },
		})

		// Cloudflare répond 200 avec `success: false` sur une erreur métier :
		// sans ce contrôle, une zone erronée passerait pour un succès.
		if (res && res.success === false) {
			const detail = res.errors.map((e) => `${e.code} ${e.message}`).join(", ")
			throw new Error(`Cloudflare a refusé « ${label} » : ${detail}`)
		}
		return res.result
	}

	/** Nom de la zone — sonde de lecture qui valide à la fois le jeton et sa portée. */
	async zoneName(): Promise<string> {
		const zone = await this.call<{ name: string }>(
			"GET",
			`/zones/${this.zoneId}`,
			"Vérifier l'accès à la zone Cloudflare",
			undefined,
			{ name: "<zone>" },
		)
		return zone.name
	}

	/**
	 * Crée un enregistrement A, ou met à jour celui qui existe déjà.
	 *
	 * Idempotent volontairement : sur un re-run après échec, écraser la cible
	 * d'un record qu'on vient de poser est sans risque, alors qu'échouer
	 * laisserait le provisioning bloqué sur une étape déjà à moitié faite.
	 */
	async upsertARecord(input: DnsRecordInput): Promise<string> {
		const existing = await this.call<{ id: string }[]>(
			"GET",
			`/zones/${this.zoneId}/dns_records?type=A&name=${encodeURIComponent(input.name)}`,
			`Chercher l'enregistrement A ${input.name}`,
			undefined,
			[],
		)

		const payload = {
			type: "A",
			name: input.name,
			content: input.content,
			proxied: input.proxied,
			ttl: 1, // 1 = automatique (imposé par Cloudflare dès que `proxied` est vrai).
		}

		if (existing && existing.length > 0) {
			const res = await this.call<{ id: string }>(
				"PUT",
				`/zones/${this.zoneId}/dns_records/${existing[0].id}`,
				`Mettre à jour l'enregistrement A ${input.name} → ${input.content}`,
				payload,
				{ id: "dry-run-dns-record" },
			)
			return res.id
		}

		const res = await this.call<{ id: string }>(
			"POST",
			`/zones/${this.zoneId}/dns_records`,
			`Créer l'enregistrement A ${input.name} → ${input.content}${input.proxied ? " (proxifié)" : " (DNS-only)"}`,
			payload,
			{ id: "dry-run-dns-record" },
		)
		return res.id
	}
}
